import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Path, Clock, Lightning, MapPin, Play } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function RideHistory() {
    const navigate = useNavigate();
    const [data, setData] = useState({ rides: [], total: 0, page: 1, pages: 1 });
    const [loading, setLoading] = useState(true);

    const fetchRides = async (page = 1) => {
        try {
            const res = await fetch(`${API}/rides/history?page=${page}&limit=15`, { credentials: 'include', headers: getH() });
            if (res.ok) setData(await res.json());
        } catch (e) { console.error('Ride history error:', e); } finally { setLoading(false); }
    };

    useEffect(() => { fetchRides(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
    const fmtDuration = (s) => { if (!s) return '--'; const m = Math.floor(s / 60); return m < 60 ? `${m}m` : `${Math.floor(m/60)}h ${m%60}m`; };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-primary text-glow">RIDE LOG</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display">Ride History</h1>
                    <p className="text-muted-foreground text-sm">{data.total} completed rides</p>
                </motion.div>

                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
                ) : data.rides.length === 0 ? (
                    <Card className="glass-card text-center py-16">
                        <Path size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground font-display">No rides yet</p>
                        <p className="text-sm text-muted-foreground mt-1">Start a ride to see your history here</p>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {data.rides.map((ride, i) => (
                            <motion.div key={ride.ride_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                                <Card className="glass-card hover:border-primary/20">
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                                    <Path size={20} className="text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-sm">{ride.distance?.toFixed(2) || 0} km</p>
                                                    <p className="text-xs text-muted-foreground flex items-center gap-2">
                                                        <Clock size={10} /> {fmtDuration(ride.duration)}
                                                        <span>•</span>
                                                        <MapPin size={10} /> {ride.vehicle_type || 'ebike'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right flex flex-col items-end gap-1">
                                                <Badge className="bg-boltz-gold/10 text-boltz-gold border-boltz-gold/20 text-xs font-mono">
                                                    +{ride.boltz_earned?.toFixed(1) || 0} BOLTZ
                                                </Badge>
                                                <p className="text-[10px] text-muted-foreground">{fmtDate(ride.end_time)}</p>
                                                {ride.gps_trace?.length >= 2 && (
                                                    <Button size="sm" variant="ghost" className="text-[10px] text-primary h-6 px-2"
                                                        onClick={() => navigate(`/ride-replay/${ride.ride_id}`)}
                                                        data-testid={`replay-${ride.ride_id}`}>
                                                        <Play size={10} className="mr-1" />Replay
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                        {data.pages > 1 && (
                            <div className="flex justify-center gap-2 pt-4">
                                {Array.from({ length: data.pages }, (_, i) => (
                                    <button key={i + 1} onClick={() => { setLoading(true); fetchRides(i + 1); }}
                                        className={`w-8 h-8 rounded-lg text-xs font-display transition-all ${data.page === i + 1 ? 'bg-primary text-black' : 'bg-white/5 text-muted-foreground hover:bg-white/10'}`}>
                                        {i + 1}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
